import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdContentCopy } from 'react-icons/md';
import { toast } from 'react-toastify';
import { createAffiliateCode } from '../../../services/affiliateService';
import Button from '../../components/buttons/Button';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import { COMMUNITY_URL } from '../../../constants';
import AffiliateTermsModal from '../../components/modals/AffiliateTermsModal';

export default function CreateAffiliateCodePage() {
  const [step, setStep] = useState<number>(0);
  const [affiliateCode, setAffiliateCode] = useState<string>('');

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  const props: ViewProps = { setStep: handleStep, affiliateCode, setAffiliateCode };

  const steps = [<CreateCodeView {...props} />, <ShareView {...props} />];

  return <StepFlow indexState={[step, setStep]} steps={steps} />;
}

interface ViewProps {
  setStep: (step: 1 | -1) => void;
  // eslint-disable-next-line
  affiliateCode: string;
  // eslint-disable-next-line
  setAffiliateCode: (code: string) => void;
}

function CreateCodeView({ setStep, setAffiliateCode }: ViewProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const affiliateLink = `${COMMUNITY_URL}/onboarding?code=${code}`;

  const handleCreateCode = () => {
    if (!termsAccepted) return;

    setAffiliateCode(code.trim());

    if (code.trim() === '') {
      setError(t('page.affiliate.create.codeError'));
      return;
    }

    if (code.length < 3) {
      setError(t('page.affiliate.create.codeLengthError'));
      return;
    }

    if (code.length > 20) {
      setError(t('page.affiliate.create.codeLengthError'));
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
      setError(t('page.affiliate.create.codeFormatError'));
      return;
    }

    createAffiliateCode(code)
      .then(() => {
        setStep(1);
      })
      .catch(() => {
        setError(t('page.affiliate.create.codeExistsError'));
      });
  };

  return (
    <>
      <StepFlowStep
        title={t('page.affiliate.create.codeTitle')}
        subtitle={t('page.affiliate.create.codeSubtitle')}
        setStep={setStep}
        showPrev={false}
        showNext={false}
        onSubmit={handleCreateCode}
        disabled={!code || !termsAccepted}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-2 font-medium">{t('page.affiliate.create.codePlaceholder')}</p>
              <input
                type="text"
                placeholder={t('page.affiliate.create.codePlaceholder')}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(null);
                }}
                className="w-full rounded-lg border p-3"
              />
            </div>

            <div>
              <p className="mb-2 font-medium">{t('page.affiliate.create.preview')}</p>
              <input
                type="text"
                className="w-full rounded-lg border border-primary bg-secondary-50 px-4 py-3 text-center font-mono text-lg font-semibold"
                value={affiliateLink}
                readOnly
              />
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}
          </div>

          <div className="border-t pt-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="affiliate-terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="affiliate-terms" className="cursor-pointer text-sm text-gray-700">
                {t('page.affiliate.create.acceptTerms')}{' '}
                <button
                  type="button"
                  className="hover:text-primary-600 font-medium text-primary hover:underline"
                  onClick={() => setModalOpen(true)}>
                  {t('page.affiliate.create.termsAndConditions')}
                </button>
              </label>
            </div>
          </div>
        </div>
      </StepFlowStep>
      <AffiliateTermsModal open={modalOpen} setOpen={setModalOpen} />
    </>
  );
}

function ShareView({ setStep, affiliateCode }: ViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const affiliateLink = `${COMMUNITY_URL}/onboarding?code=${affiliateCode}`;

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(affiliateCode);
      toast.success(t('toast.success.copiedToClipboard'));
    } catch (err) {
      console.error('Failed to copy code:', err);
      toast.error('Failed to copy code to clipboard');
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      toast.success(t('toast.success.copiedToClipboard'));
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link to clipboard');
    }
  };

  return (
    <StepFlowStep
      title={t('page.affiliate.create.shareTitle')}
      subtitle={t('page.affiliate.create.shareSubtitle')}
      setStep={setStep}
      showNext={false}
      showPrev={false}
      onSubmit={() => navigate('/affiliate')}
      submitText={t('general.continue')}>
      <div className="mb-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('page.affiliate.modal.codeLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 rounded-full border border-primary bg-secondary-50 px-4 py-2 text-center font-mono text-lg font-semibold"
              value={affiliateCode}
              readOnly
            />
            <Button variant="secondary" onClick={copyCodeToClipboard} className="rounded-full p-3">
              <MdContentCopy className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('page.affiliate.modal.linkLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 overflow-x-scroll rounded-full border border-primary bg-secondary-50 px-4 py-2 text-sm"
              value={affiliateLink}
              readOnly
            />
            <Button variant="secondary" onClick={copyLinkToClipboard} className="rounded-full p-3">
              <MdContentCopy className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </StepFlowStep>
  );
}
